"use client"

import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  CSSProperties,
} from "react"
import { useMediaQuery } from "react-responsive"
import { createClient } from "@/lib/supabase/client"

/**
 * StoryPageFlipbook — Full file (small fixes requested)
 *
 * Fixes applied:
 *  - Uses story.video_url (and local videoUrl state) to decide whether to show "Open Video Fullscreen"
 *    or "Generate Video".
 *  - Cover image rendering updated so mobile uses object-fit: contain (less zoomed); desktop keeps
 *    full-bleed feel.
 *  - Back cover displays only the label "Back cover", has effects, and matches the page size. It is
 *    shown on desktop (same behavior as before).
 *
 * Nothing else changed except the minimal structural/CSS updates required to implement the above.
 */

// ----------------------
// Types & helpers
// ----------------------
type Seg = {
  image_url?: string | null
  image_description?: string | null
  story_segment?: string | null
  video_description?: string | null
  music_score?: string | null
  music_url?: string | null
  [k: string]: any
}

const normalizeSegments = (raw: any): Seg[] => {
  try {
    if (!Array.isArray(raw)) return []
    return raw.map((s: any, i: number) => {
      if (!s || typeof s !== "object") {
        console.warn(`[normalizeSegments] segment ${i} invalid — using empty segment`)
        return {
          image_url: null,
          image_description: null,
          story_segment: null,
          video_description: null,
          music_score: null,
          music_url: null,
        }
      }
      return {
        image_url: s.image_url ?? null,
        image_description: s.image_description ?? null,
        story_segment: s.story_segment ?? null,
        video_description: s.video_description ?? null,
        music_score: s.music_score ?? null,
        music_url: s.music_url ?? null,
        ...s,
      }
    })
  } catch (e) {
    console.error("[normalizeSegments] unexpected error", e)
    return []
  }
}

/** Limit text to roughly 100 words (keeps whole words) */
const limitWords = (text: string | null | undefined, max = 100) => {
  if (!text) return ""
  const words = String(text).split(/\s+/).filter(Boolean)
  if (words.length <= max) return words.join(" ")
  return words.slice(0, max).join(" ") + "…"
}

/** Safe image to dataURL (for PDF export) */
async function fetchImageToDataURLSafe(url: string): Promise<string> {
  try {
    const resp = await fetch(url, { mode: "cors" })
    if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`)
    const blob = await resp.blob()
    if (!blob || !blob.size) throw new Error("Empty blob")
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = (err) => reject(err)
      reader.readAsDataURL(blob)
    })
  } catch (err) {
    console.warn("[fetchImageToDataURLSafe] fetch failed or CORS", err)
    // transparent placeholder
    return "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII="
  }
}

async function loadImage(dataURL: string): Promise<HTMLImageElement | null> {
  return await new Promise((resolve) => {
    try {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => resolve(null)
      img.src = dataURL
    } catch {
      resolve(null)
    }
  })
}

// ----------------------
// FlipBookAnimated component (SSR-safe, interactive)
// ----------------------
type FlipBookHandle = {
  flipNext: () => void
  flipPrev: () => void
  getCurrentPageIndex: () => number
}

const FlipBookAnimated = forwardRef<
  FlipBookHandle,
  {
    segments: Seg[]
    coverTitle?: string
    coverImageUrl?: string | null
    coverFullName?: string
    isMobile?: boolean
    onRequestFlip?: (dir: "next" | "prev") => void
  }
>(({ segments, coverTitle, coverImageUrl, coverFullName, isMobile, onRequestFlip }, ref) => {
  const segs = Array.isArray(segments) ? segments : []
  const contentCount = segs.length

  // viewIndex semantics:
  // Mobile: 0 = cover, 1..N => segments[0..N-1]
  // Desktop: 0 = cover (right page displayed), >=1 => spread index where spread 1 => segments[0,1]
  const [viewIndex, setViewIndex] = useState<number>(0)
  const [isFlipping, setIsFlipping] = useState(false)
  const [flipDir, setFlipDir] = useState<"next" | "prev" | null>(null)

  // pointer drag state
  const pointerRef = useRef<{
    active: boolean
    startX: number
    startY: number
    lastX: number
    lastY: number
    target: "left" | "right" | null
  }>({ active: false, startX: 0, startY: 0, lastX: 0, lastY: 0, target: null })

  useImperativeHandle(
    ref,
    () => ({
      flipNext: () => {
        if (isFlipping) return
        if (isMobile) {
          if (viewIndex >= contentCount) return
          setFlipDir("next")
          setIsFlipping(true)
        } else {
          const maxSpread = Math.max(1, Math.ceil(contentCount / 2))
          if (viewIndex >= maxSpread) return
          setFlipDir("next")
          setIsFlipping(true)
        }
      },
      flipPrev: () => {
        if (isFlipping) return
        if (viewIndex <= 0) return
        setFlipDir("prev")
        setIsFlipping(true)
      },
      getCurrentPageIndex: () => {
        if (isMobile) {
          return Math.max(0, Math.min(contentCount - 1, Math.max(0, viewIndex - 1)))
        } else {
          if (viewIndex === 0) return 0
          const rightIndex = (viewIndex - 1) * 2 + 1
          return Math.min(Math.max(0, rightIndex), contentCount - 1)
        }
      },
    }),
    [isFlipping, viewIndex, contentCount, isMobile]
  )

  // commit after CSS animation
  useEffect(() => {
    if (!isFlipping || !flipDir) return
    const duration = isMobile ? 420 : 560
    const tid = setTimeout(() => {
      if (isMobile) {
        if (flipDir === "next") setViewIndex((v) => Math.min(contentCount, v + 1))
        else setViewIndex((v) => Math.max(0, v - 1))
      } else {
        const maxSpread = Math.max(1, Math.ceil(contentCount / 2))
        if (flipDir === "next") setViewIndex((v) => Math.min(maxSpread, v + 1))
        else setViewIndex((v) => Math.max(0, v - 1))
      }
      setIsFlipping(false)
      setFlipDir(null)
    }, duration)
    return () => clearTimeout(tid)
  }, [isFlipping, flipDir, isMobile, contentCount])

  // pointer handlers for swipe/drag
  useEffect(() => {
    const el = document.getElementById("book-interactive-area")
    if (!el) return
    const onPointerDown = (ev: PointerEvent) => {
      if (ev.button && ev.button !== 0) return
      pointerRef.current.active = true
      pointerRef.current.startX = ev.clientX
      pointerRef.current.startY = ev.clientY
      pointerRef.current.lastX = ev.clientX
      pointerRef.current.lastY = ev.clientY
      const rect = el.getBoundingClientRect()
      pointerRef.current.target = ev.clientX < rect.left + rect.width / 2 ? "left" : "right"
      try { (ev.target as Element).setPointerCapture(ev.pointerId) } catch (err) {}
    }
    const onPointerMove = (ev: PointerEvent) => {
      if (!pointerRef.current.active) return
      pointerRef.current.lastX = ev.clientX
      pointerRef.current.lastY = ev.clientY
    }
    const onPointerUp = (ev: PointerEvent) => {
      if (!pointerRef.current.active) return
      pointerRef.current.active = false
      const dx = ev.clientX - pointerRef.current.startX
      const dy = ev.clientY - pointerRef.current.startY
      const absDx = Math.abs(dx)
      const absDy = Math.abs(dy)
      if (absDx > 40 && absDx > absDy) {
        if (dx > 0) {
          if (viewIndex > 0) {
            setFlipDir("prev")
            setIsFlipping(true)
            onRequestFlip?.("prev")
          }
        } else {
          if (isMobile) {
            if (viewIndex < contentCount) {
              setFlipDir("next")
              setIsFlipping(true)
              onRequestFlip?.("next")
            }
          } else {
            const maxSpread = Math.max(1, Math.ceil(contentCount / 2))
            if (viewIndex < maxSpread) {
              setFlipDir("next")
              setIsFlipping(true)
              onRequestFlip?.("next")
            }
          }
        }
      } else {
        const rect = el.getBoundingClientRect()
        const clickedRight = ev.clientX > rect.left + rect.width / 2
        if (clickedRight) {
          if (isMobile) {
            if (viewIndex < contentCount) {
              setFlipDir("next")
              setIsFlipping(true)
              onRequestFlip?.("next")
            }
          } else {
            const maxSpread = Math.max(1, Math.ceil(contentCount / 2))
            if (viewIndex < maxSpread) {
              setFlipDir("next")
              setIsFlipping(true)
              onRequestFlip?.("next")
            }
          }
        } else {
          if (viewIndex > 0) {
            setFlipDir("prev")
            setIsFlipping(true)
            onRequestFlip?.("prev")
          }
        }
      }
      try { (ev.target as Element).releasePointerCapture(ev.pointerId) } catch {}
    }

    el.addEventListener("pointerdown", onPointerDown)
    window.addEventListener("pointermove", onPointerMove)
    window.addEventListener("pointerup", onPointerUp)
    return () => {
      el.removeEventListener("pointerdown", onPointerDown)
      window.removeEventListener("pointermove", onPointerMove)
      window.removeEventListener("pointerup", onPointerUp)
    }
  }, [isMobile, viewIndex, contentCount, onRequestFlip])

  /* CSS for the flipbook */
  const css = `
    .flipbook-shell { width:100%; display:flex; justify-content:center; align-items:flex-start; }
    .book-frame { position: relative; max-width: 1100px; width:100%; display:flex; justify-content:center; align-items:center; }
    .book-spread { display:flex; gap:20px; align-items:stretch; justify-content:center; position:relative; }
    .page-slot { width:520px; height:720px; flex:0 0 520px; position:relative; border-radius:14px; overflow:hidden; }
    @media(max-width:900px) {
      .page-slot { width:100%; height:620px; flex:1 1 auto; }
      .book-spread { flex-direction:column; gap:12px; align-items:center }
    }

    /* Gold rim and sheen */
    .page-shell {
      position:relative;
      width:100%;
      height:100%;
      border-radius:14px;
      overflow:hidden;
      background: linear-gradient(180deg,#fffdf6,#fff8e6);
      box-shadow: 0 16px 60px rgba(0,0,0,0.6);
      border: 1px solid rgba(255,215,64,0.18);
    }
    .page-shell .gold-rim {
      position:absolute; inset:0; border-radius:14px; pointer-events:none;
      box-shadow:
        0 0 40px rgba(255,215,64,0.36),
        inset 0 0 22px rgba(255,215,64,0.14);
      mix-blend-mode: screen;
      z-index:4;
    }
    .page-shell::after {
      content:""; position:absolute; inset:-40%; background: radial-gradient(ellipse at center, rgba(255,238,150,0.03), rgba(255,210,40,0.02) 6%, transparent 14%); transform: rotate(-12deg); animation: sheen 6s linear infinite; z-index:1; pointer-events:none;
    }
    @keyframes sheen {
      0% { transform: translateX(-40%) rotate(-12deg); opacity:0.58 }
      50% { transform: translateX(40%) rotate(-12deg); opacity:0.95 }
      100% { transform: translateX(-40%) rotate(-12deg); opacity:0.58 }
    }

    /* spine */
    .book-spine { position:absolute; left: calc(50% - 4px); top:0; bottom:0; width:8px; background: linear-gradient(180deg, rgba(0,0,0,0.12), rgba(0,0,0,0.02)); border-radius:6px; z-index:5; box-shadow: inset 0 8px 20px rgba(0,0,0,0.3); }

    /* flip overlay */
    .flip-overlay {
      position:absolute; top:0; height:100%; width:50%; z-index:80; backface-visibility:hidden; transform-style:preserve-3d; overflow:hidden; border-radius:12px;
      box-shadow: 0 20px 80px rgba(0,0,0,0.55);
    }
    .flip-overlay.right { right: calc(50% - 10px); transform-origin:left center; }
    .flip-overlay.left { left: calc(50% - 10px); transform-origin:right center; }

    /* desktop flip anims */
    .flip-next { animation: bookFlipNext 560ms cubic-bezier(.2,.9,.3,1) forwards; }
    .flip-prev { animation: bookFlipPrev 560ms cubic-bezier(.2,.9,.3,1) forwards; }
    @keyframes bookFlipNext {
      0% { transform: perspective(2000px) rotateY(0deg); }
      45% { transform: perspective(2000px) rotateY(-95deg) translateX(-6%); }
      100% { transform: perspective(2000px) rotateY(-180deg) translateX(-12%); opacity:0; }
    }
    @keyframes bookFlipPrev {
      0% { transform: perspective(2000px) rotateY(0deg); }
      45% { transform: perspective(2000px) rotateY(95deg) translateX(6%); }
      100% { transform: perspective(2000px) rotateY(180deg) translateX(12%); opacity:0; }
    }

    /* mobile flip */
    .mobile-next { animation: mobileNext 420ms cubic-bezier(.2,.9,.3,1) forwards; }
    .mobile-prev { animation: mobilePrev 420ms cubic-bezier(.2,.9,.3,1) forwards; }
    @keyframes mobileNext { 0% { transform: translateY(0) } 100% { transform: translateY(-18%) scale(.98); opacity:0 } }
    @keyframes mobilePrev { 0% { transform: translateY(0) } 100% { transform: translateY(18%) scale(.98); opacity:0 } }

    /* page content layout */
    .page-top-image { height:44%; min-height:140px; overflow:hidden; display:flex; align-items:center; justify-content:center; background:linear-gradient(180deg,#f7f1e0,#fff); position:relative; z-index:3; }
    .page-top-image img { width:100%; height:100%; object-fit:cover; display:block; }

    .page-body { padding:18px 22px; height: calc(56% - 18px); display:flex; flex-direction:column; justify-content:flex-start; overflow:hidden; position:relative; z-index:3;}
    .page-title { margin:0; font-size:16px; color:#2b2b2b; }
    .page-text { margin-top:8px; color:#111; line-height:1.55; font-size:14px; overflow:hidden; }

    /* cover author pill */
    .cover-author {
      position:absolute; bottom:18px; left:18px; background:#000; color:#fff; padding:8px 12px; border-radius:12px; z-index:8; font-weight:700; box-shadow: 0 6px 18px rgba(0,0,0,0.6);
      border: 1px solid rgba(255,215,64,0.12);
    }

     .cover-layer { position:absolute; inset:0; z-index:0; background:#000; }
    .cover-layer img { width:100%; height:100%; object-fit:cover; object-position:center center; display:block; }

    /* Make cover fill the whole page on mobile too (cover, centered) */
    @media (max-width:900px) {
      .cover-layer img { object-fit:cover; object-position:center center; }
    }

    /* ensure page content sits above cover image */
    .page-content { position:relative; z-index:6; height:100%; }

    .page-shell .gold-sparks { position:absolute; inset:0; pointer-events:none; z-index:2; }
    .gold-sparks .spark { position:absolute; width:6px; height:6px; border-radius:50%; background: radial-gradient(circle, rgba(255,245,190,1) 0%, rgba(255,200,60,1) 50%, rgba(255,200,60,0) 100%); animation: sparkFloat 2.8s linear infinite; opacity:0.9 }
    @keyframes sparkFloat {
      0% { transform: translateY(0) scale(0.8); opacity:1 }
      50% { transform: translateY(-20px) scale(1.25); opacity:0.7 }
      100% { transform: translateY(0) scale(0.8); opacity:1 }
    }

    /* Back cover (black with many gold effects) */
    .back-cover {
      background: linear-gradient(180deg,#030202,#060303);
      border: 1px solid rgba(255,215,64,0.12);
      box-shadow: 0 24px 80px rgba(0,0,0,0.85), inset 0 8px 30px rgba(255,215,64,0.02);
      display:flex; align-items:center; justify-content:center; color:#EEDDA2; font-weight:800;
      position:relative; overflow:hidden; border-radius:14px;
    }
    .back-cover .rings { position:absolute; inset:0; z-index:2; pointer-events:none; }
    .back-cover .ring { position:absolute; border-radius:50%; border: 1px solid rgba(255,215,64,0.08); box-shadow: 0 0 30px rgba(255,215,64,0.08); filter: blur(6px); animation: ringPulse 7s linear infinite; opacity:0.9; }
    .back-cover .ring.r1 { width:120%; height:120%; left:-10%; top:-10%; animation-duration:9s; }
    .back-cover .ring.r2 { width:80%; height:80%; left:10%; top:10%; animation-duration:6.2s; transform: rotate(6deg); }
    .back-cover .filament { position:absolute; width:2px; height:60%; left:50%; top:20%; background: linear-gradient(180deg, rgba(255,240,180,0.9), rgba(255,200,60,0.15)); transform-origin:bottom center; animation: filamentWave 2.8s ease-in-out infinite; opacity:0.9; }
    @keyframes ringPulse {
      0% { transform: scale(.98) rotate(0deg); opacity:0.85 }
      50% { transform: scale(1.02) rotate(2deg); opacity:1 }
      100% { transform: scale(.98) rotate(0deg); opacity:0.85 }
    }
    @keyframes filamentWave {
      0% { transform: translateY(0) rotate(-3deg) scaleY(0.98) }
      50% { transform: translateY(-8px) rotate(3deg) scaleY(1.05) }
      100% { transform: translateY(0) rotate(-3deg) scaleY(0.98) }
    }
  `

 // helpers for indices
  const desktopLeftIndex = (spread: number) => {
    if (spread <= 0) return null
    return (spread - 1) * 2
  }
  const desktopRightIndex = (spread: number) => {
    if (spread <= 0) return null
    return (spread - 1) * 2 + 1
  }

  const getOverlayContent = (mode: "next" | "prev") => {
    if (isMobile) {
      const idx = Math.max(0, Math.min(contentCount - 1, viewIndex - 1))
      return idx >= 0 ? segs[idx] ?? null : null
    } else {
      if (mode === "next") {
        if (viewIndex === 0) return { isCover: true as const }
        const r = desktopRightIndex(viewIndex)
        return r !== null ? segs[r] ?? null : null
      } else {
        const l = desktopLeftIndex(viewIndex)
        return l !== null ? segs[l] ?? null : null
      }
    }
  }

  const PageShell: React.FC<{ fullCover?: boolean; coverUrl?: string | null; children?: React.ReactNode; className?: string }> = ({ fullCover, coverUrl, children, className }) => {
    // Instead of background-image (which aggressively crops), render an <img> layer so we can use
    // object-fit:contain on mobile (less zoom) and cover on desktop.
    return (
      <div className={`page-shell ${className ?? ""}`}>
        <div className="gold-rim" />
        <div className="gold-sparks" aria-hidden>
          {Array.from({ length: 7 }).map((_, i) => {
            const left = Math.round(5 + Math.random() * 90)
            const top = Math.round(5 + Math.random() * 90)
            const dur = 2200 + Math.round(Math.random() * 1200)
            return <div key={i} className="spark" style={{ left: `${left}%`, top: `${top}%`, animationDuration: `${dur}ms`, opacity: 0.9 }} />
          })}
        </div>

        {fullCover && coverUrl ? (
          <div className="cover-layer" aria-hidden>
            <img src={coverUrl} alt="Cover" onError={() => console.warn("[PageShell] cover image failed", coverUrl)} />
          </div>
        ) : null}

        <div className="page-content">
          {children}
        </div>
      </div>
    )
  }

  // Back cover component (keeps only "Back cover" label)
  const BackCover: React.FC = () => {
    return (
      <div className="back-cover">
        <div style={{ zIndex: 8, textAlign: "center" }}>
          <div style={{ fontSize: 20, letterSpacing: 1.6 }}>Back cover</div>
        </div>

        {/* rings & filaments */}
        <div className="rings" aria-hidden>
          <div className="ring r1" />
          <div className="ring r2" />
          {Array.from({ length: 5 }).map((_, i) => {
            const leftPct = 10 + i * 18
            const delay = (i % 3) * 0.18
            return <div key={i} className="filament" style={{ left: `${leftPct}%`, animationDelay: `${delay}s` }} />
          })}
        </div>

        <svg width="100%" height="100%" style={{ position: "absolute", top: 0, left: 0, zIndex: 3, pointerEvents: "none" }}>
          <defs>
            <linearGradient id="bgGlow" x1="0" x2="1">
              <stop offset="0%" stopColor="rgba(255,240,180,0.06)" />
              <stop offset="50%" stopColor="rgba(255,200,60,0.03)" />
              <stop offset="100%" stopColor="rgba(255,240,180,0.04)" />
            </linearGradient>
            <filter id="fBlur" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="18" />
            </filter>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#bgGlow)" filter="url(#fBlur)" opacity="0.12" />
        </svg>
      </div>
    )
  }

  const ContentPage: React.FC<{ idx: number | null }> = ({ idx }) => {
    if (idx === null || idx === undefined) {
      return (
        <PageShell>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#8b7f6b" }}>(No content)</div>
        </PageShell>
      )
    }
    const seg = segs[idx] ?? {}
    return (
      <PageShell>
        <div className="page-top-image">
          {seg.image_url ? <img src={seg.image_url} alt={`Scene ${idx + 1}`} onError={() => console.warn("[ContentPage] image failed", seg.image_url)} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#b59e6a" }}>Image</div>}
        </div>
        <div className="page-body">
          <h3 className="page-title">Scene {idx + 1}</h3>
          <div className="page-text">{limitWords(seg.story_segment ?? "", 100)}</div>
        </div>
      </PageShell>
    )
  }

  // render mobile or desktop separately
  if (isMobile) {
    const mobilePageIdx = Math.max(0, viewIndex - 1)
    const showCover = viewIndex === 0
    const mobileClass = isFlipping ? (flipDir === "next" ? "mobile-next" : "mobile-prev") : ""
    return (
      <div id="book-interactive-area" className="flipbook-shell" style={{ width: "100%" }}>
        <style>{css}</style>
        <div className="book-frame" style={{ width: "100%" }}>
          <div className="book-spread" style={{ width: "100%", justifyContent: "center" }}>
            <div className="page-slot" style={{ width: "100%" }}>
              <div className={mobileClass} style={{ width: "100%", height: "100%" }}>
                {showCover ? (
                  <PageShell fullCover coverUrl={coverImageUrl ?? null}>
                    <div className="cover-author">{coverFullName ?? ""}</div>
                    <div style={{ position: "absolute", bottom: 72, left: 20, right: 20, color: "#FFF8EA", fontSize: 18, fontWeight: 900, textShadow: "0 4px 18px rgba(0,0,0,0.5)" }}>
                      {coverTitle ?? "Untitled"}
                    </div>
                  </PageShell>
                ) : (
                  <ContentPage idx={mobilePageIdx} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  } else {
    const isCover = viewIndex === 0
    const leftIdx = desktopLeftIndex(viewIndex)
    const rightIdx = desktopRightIndex(viewIndex)
    const overlayMode = flipDir ?? null
    const overlayContent = overlayMode ? getOverlayContent(overlayMode) : null
    const overlayClass = isFlipping ? (flipDir === "next" ? "flip-overlay right flip-next" : "flip-overlay left flip-prev") : ""

    return (
      <div id="book-interactive-area" className="flipbook-shell" style={{ width: "100%" }}>
        <style>{css}</style>
        <div className="book-frame" style={{ width: "100%", maxWidth: 1100 }}>
          <div style={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}>
            <div className="book-spread" style={{ width: "100%", justifyContent: "center" }}>
              {/* LEFT */}
              <div className="page-slot" aria-hidden={isCover}>
                {isCover ? (
                  <BackCover />
                ) : (
                  <ContentPage idx={leftIdx} />
                )}
              </div>

              {/* spine */}
              <div style={{ width: 8 }}><div className="book-spine" /></div>

              {/* RIGHT (primary) */}
              <div className="page-slot" style={{ position: "relative" }}>
                {isCover ? (
                  <PageShell fullCover coverUrl={coverImageUrl ?? null}>
                    <div className="cover-author">{coverFullName ?? ""}</div>
                    <div style={{ position: "absolute", bottom: 60, left: 28, right: 28, color: "#111", fontWeight: 900, fontSize: 30, textShadow: "0 6px 28px rgba(255,215,64,0.08)" }}>
                      {coverTitle ?? "Untitled"}
                    </div>
                  </PageShell>
                ) : (
                  <ContentPage idx={rightIdx} />
                )}

                {/* flipping overlay displayed while animating */}
                {isFlipping && flipDir ? (
                  <div className={overlayClass} style={{ pointerEvents: "none", top: 0 }}>
                    <div style={{ width: "100%", height: "100%" }}>
                      {overlayContent && (overlayContent as any).isCover ? (
                        <PageShell fullCover coverUrl={coverImageUrl ?? null} />
                      ) : overlayContent ? (
                        <PageShell>
                          <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            {(overlayContent as Seg).image_url ? (
                              <div className="page-top-image">
                                <img src={(overlayContent as Seg).image_url!} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              </div>
                            ) : (
                              <div className="page-top-image">Image</div>
                            )}
                            <div className="page-body">
                              <h3 className="page-title">Scene</h3>
                              <div className="page-text">{limitWords((overlayContent as Seg).story_segment ?? "", 100)}</div>
                            </div>
                          </div>
                        </PageShell>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
})
FlipBookAnimated.displayName = "FlipBookAnimated"

// ----------------------
// Main StoryPage component
// ----------------------
export default function StoryPageWrapper({ params }: { params: { story_id?: string } }) {
  const storyId = params?.story_id ?? ""
  return <StoryPage storyId={storyId} />
}

function StoryPage({ storyId }: { storyId: string }) {
  const supabase = createClient()

  // refs
  const flipBookRef = useRef<FlipBookHandle | null>(null)
  const playbackMusicRef = useRef<HTMLAudioElement | null>(null)
  const playbackTTSRef = useRef<HTMLAudioElement | null>(null)

  // state
  const [story, setStory] = useState<any | null>(null)
  const [segments, setSegments] = useState<Seg[]>([])
  const [currentUser, setCurrentUser] = useState<any | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [videoIsFullScreen, setVideoIsFullScreen] = useState(false)
  const [likesCount, setLikesCount] = useState<number>(0)
  const [voice, setVoice] = useState<string>("default")
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false)
  const [isPublicUpdating, setIsPublicUpdating] = useState<boolean>(false)
  const [isLiking, setIsLiking] = useState<boolean>(false)
  const [liked, setLiked] = useState<boolean>(false)
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const [isGeneratingMusic, setIsGeneratingMusic] = useState<boolean>(false)
  const [isGeneratingAllMusic, setIsGeneratingAllMusic] = useState<boolean>(false)
  const [stereoMix, setStereoMix] = useState<boolean>(false)
  const [fullName, setFullName] = useState<string>("")
  const [isPlayingAllMusic, setIsPlayingAllMusic] = useState<boolean>(false)
  const [stopAllMusicSignal, setStopAllMusicSignal] = useState<number>(0)
  const [useVideoBlobWorkaround, setUseVideoBlobWorkaround] = useState<boolean>(false)
  const [selectedLanguage, setSelectedLanguage] = useState<string>("en")
  const [translationLoading, setTranslationLoading] = useState<boolean>(false)

  const isMobile = useMediaQuery({ maxWidth: 820 })

  const languages = [
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "fr", label: "French" },
    { code: "de", label: "German" },
    { code: "zh", label: "Chinese" },
    { code: "ja", label: "Japanese" },
  ]

  // hydration
  useEffect(() => {
    console.log("[hydration] client mounted")
  }, [])

  // load story & user
  useEffect(() => {
    let mounted = true
    async function load() {
      try {
        const authResp = await supabase.auth.getUser()
        const current = authResp?.data?.user ?? null
        if (!mounted) return
        setCurrentUser(current)

        if (!storyId) {
          if (mounted) setError("Missing story id")
          return
        }

        console.log("[load] fetching story row:", storyId)
        const { data: storyRow, error: storyErr } = await supabase.from("stories").select("*").eq("id", storyId).single()
        if (!mounted) return
        if (storyErr) {
          if (mounted) setError(storyErr.message || "Failed to load story")
          return
        }

        const normalized = normalizeSegments(storyRow?.content)
        if (mounted) {
          setStory(storyRow ?? null)
          setSegments(normalized)
          // ensure we set videoUrl from DB column so UI reflects story.video_url
          setVideoUrl(storyRow?.video_url ?? null)
          setLikesCount(Number(storyRow?.likes_count ?? 0))
        }

        if (storyRow?.user_id) {
          const { data: owner, error: ownerErr } = await supabase.from("users").select("first_name,last_name").eq("id", storyRow.user_id).single()
          if (!mounted) return
          if (!ownerErr && owner) {
            const n = `${owner.first_name ?? ""} ${owner.last_name ?? ""}`.trim()
            setFullName(n)
          } else setFullName("")
        } else setFullName("")

        if (current?.id) {
          const { data: likeRow } = await supabase.from("story_likes").select("id").eq("story_id", storyId).eq("user_id", current.id).single()
          if (!mounted) return
          setLiked(!!likeRow)
        }
      } catch (err: any) {
        if (mounted) setError(String(err?.message || err))
      }
    }

    load()
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId])

  // translation handler (unchanged)
  useEffect(() => {
    if (selectedLanguage === "en") {
      setSegments(normalizeSegments(story?.content))
      setTranslationLoading(false)
      setError(null)
      return
    }
    if (!story) return
    let mounted = true
    async function loadTranslation() {
      if (mounted) {
        setTranslationLoading(true)
        setError(null)
      }
      try {
        const { data: translation } = await supabase
          .from("story_translations")
          .select("*")
          .eq("story_id", storyId)
          .eq("language_code", selectedLanguage)
          .single()
        if (!mounted) return
        if (translation && Array.isArray(translation.content)) {
          if (mounted) {
            setStory((s: any) => ({ ...s, title: translation.title ?? s?.title }))
            setSegments(normalizeSegments(translation.content))
            setTranslationLoading(false)
          }
          return
        }
        const res = await fetch("/api/translate-story", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ story_id: storyId, language: selectedLanguage }),
        })
        if (!res.ok) {
          const text = await res.text()
          if (mounted) {
            setError(`Translation failed: ${res.status} ${text}`)
            setSegments(normalizeSegments(story?.content))
            setTranslationLoading(false)
          }
          return
        }
        const payload = await res.json()
        if (payload && payload.title && Array.isArray(payload.content)) {
          if (mounted) {
            setStory((s: any) => ({ ...s, title: payload.title ?? s?.title }))
            setSegments(normalizeSegments(payload.content))
            setTranslationLoading(false)
          }
          return
        }
        const { data: fresh } = await supabase
          .from("story_translations")
          .select("*")
          .eq("story_id", storyId)
          .eq("language_code", selectedLanguage)
          .single()
        if (!mounted) return
        if (fresh && Array.isArray(fresh.content)) {
          if (mounted) {
            setStory((s: any) => ({ ...s, title: fresh.title ?? s?.title }))
            setSegments(normalizeSegments(fresh.content))
          }
        } else {
          if (mounted) {
            setError("Translation not available yet. Showing original.")
            setSegments(normalizeSegments(story?.content))
          }
        }
      } catch (err: any) {
        if (mounted) {
          setError(String(err?.message || err))
          setSegments(normalizeSegments(story?.content))
        }
      } finally {
        if (mounted) setTranslationLoading(false)
      }
    }
    loadTranslation()
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage, storyId, story?.content])

  // flip helpers
  const goNext = () => {
    try {
      flipBookRef.current?.flipNext()
    } catch (e) {
      console.warn("[goNext] failed", e)
    }
  }
  const goPrev = () => {
    try {
      flipBookRef.current?.flipPrev()
    } catch (e) {
      console.warn("[goPrev] failed", e)
    }
  }
  const getCurrentSegIndex = () => {
    try {
      const idx = flipBookRef.current?.getCurrentPageIndex?.() ?? 0
      return Math.max(0, Number(idx) || 0)
    } catch (e) {
      console.warn("[getCurrentSegIndex] failed", e)
      return 0
    }
  }

  // like / public toggles
  const togglePublic = async () => {
    if (!story?.id) return
    setIsPublicUpdating(true)
    try {
      const { error } = await supabase.from("stories").update({ is_public: !story.is_public }).eq("id", story.id)
      if (!error) setStory((s: any) => ({ ...s, is_public: !s.is_public }))
    } catch (err) {
      console.error("[togglePublic] error", err)
    } finally {
      setIsPublicUpdating(false)
    }
  }

  const handleLike = async () => {
    if (!currentUser?.id || !story?.id) return
    setIsLiking(true)
    try {
      if (liked) {
        await supabase.from("story_likes").delete().eq("story_id", story.id).eq("user_id", currentUser.id)
        await supabase.from("stories").update({ likes_count: Math.max(0, Number(likesCount) - 1) }).eq("id", story.id)
        setLiked(false)
        setLikesCount((c) => Math.max(0, c - 1))
      } else {
        await supabase.from("story_likes").insert([{ story_id: story.id, user_id: currentUser.id }])
        await supabase.from("stories").update({ likes_count: Number(likesCount) + 1 }).eq("id", story.id)
        setLiked(true)
        setLikesCount((c) => c + 1)
      }
    } catch (err) {
      console.error("[handleLike] error", err)
    } finally {
      setIsLiking(false)
    }
  }

  // TTS & read aloud
  const readAloud = async () => {
    const segIndex = getCurrentSegIndex()
    const seg = segments?.[segIndex]
    if (!seg?.story_segment) {
      console.warn("[readAloud] no story_segment at index", segIndex)
      return
    }
    setIsSpeaking(true)
    try {
      const res = await fetch("/api/tts", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: seg.story_segment, voice }) })
      if (!res.ok) {
        console.warn("[TTS] server returned non-OK", res.status)
        setIsSpeaking(false)
        return
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const audio = new Audio(url)
      audio.crossOrigin = "anonymous"
      playbackTTSRef.current = audio
      audio.onended = () => {
        setIsSpeaking(false)
        playbackTTSRef.current = null
        try { URL.revokeObjectURL(url) } catch {}
      }
      audio.onerror = (e) => {
        console.error("[readAloud] audio error", e)
        setIsSpeaking(false)
        playbackTTSRef.current = null
        try { URL.revokeObjectURL(url) } catch {}
      }
      await audio.play().catch((e) => {
        console.warn("[TTS] playback failed", e)
        setIsSpeaking(false)
        playbackTTSRef.current = null
      })
    } catch (err) {
      console.error("[TTS] unexpected error", err)
      setIsSpeaking(false)
    }
  }

  const readAloudWithMusic = async () => {
  const segIndex = getCurrentSegIndex()
  const seg = segments?.[segIndex]
  if (!seg?.story_segment) {
    console.warn("[readAloudWithMusic] no story_segment")
    return
  }

  try {
    // fetch story.music_url from Supabase
    const { data: story, error: fetchErr } = await supabase
      .from("stories")
      .select("music_url")
      .eq("id", storyId)
      .single()

    if (fetchErr) {
      console.error("[readAloudWithMusic] failed to fetch story.music_url:", fetchErr)
      await readAloud()
      return
    }

    const musicUrl = story?.music_url
    if (!musicUrl) {
      // fallback: no music_url stored
      await readAloud()
      return
    }

    setIsSpeaking(true)

    // request TTS
    const ttsRes = await fetch("/api/tts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: seg.story_segment, voice }),
    })

    if (!ttsRes.ok) {
      setIsSpeaking(false)
      return
    }

    const ttsBlob = await ttsRes.blob()
    const ttsUrl = URL.createObjectURL(ttsBlob)
    const ttsAudio = new Audio(ttsUrl)
    ttsAudio.crossOrigin = "anonymous"

    const musicAudio = new Audio(musicUrl)
    musicAudio.crossOrigin = "anonymous"
    musicAudio.loop = true
    musicAudio.volume = 0.5

    // stop any currently playing audio
    try { playbackMusicRef.current?.pause() } catch {}
    try { playbackTTSRef.current?.pause() } catch {}

    await musicAudio.play().catch((e) => console.warn("[Music] playback failed", e))
    playbackMusicRef.current = musicAudio

    ttsAudio.onplay = () => {
      try {
        fadeVolume(musicAudio, musicAudio.volume ?? 0.5, 0.16, 220)
      } catch {
        try { musicAudio.volume = 0.16 } catch {}
      }
    }

    ttsAudio.onended = () => {
      try {
        fadeVolume(musicAudio, musicAudio.volume ?? 0.16, 0.5, 420)
      } catch {}
      setTimeout(() => {
        try { musicAudio.pause() } catch {}
        setIsSpeaking(false)
        playbackTTSRef.current = null
        playbackMusicRef.current = null
        try { URL.revokeObjectURL(ttsUrl) } catch {}
      }, 400)
    }

    playbackTTSRef.current = ttsAudio
    await ttsAudio.play().catch((e) => {
      console.warn("[TTS] playback failed", e)
      try { musicAudio.pause() } catch {}
      setIsSpeaking(false)
      playbackTTSRef.current = null
      playbackMusicRef.current = null
      try { URL.revokeObjectURL(ttsUrl) } catch {}
    })
  } catch (err) {
    console.error("[Music+TTS] unexpected error", err)
    setIsSpeaking(false)
  }
  }
  function fadeVolume(audio: HTMLMediaElement, from: number, to: number, duration = 300) {
    if (!audio) return
    const start = Date.now()
    const diff = to - from
    try { audio.volume = from } catch {}
    const step = () => {
      const t = Math.min(1, (Date.now() - start) / duration)
      try { audio.volume = Math.min(1, Math.max(0, from + diff * t)) } catch {}
      if (t < 1) requestAnimationFrame(step)
    }
    step()
  }

  // music save/generation (kept)
  async function saveMusicForPage(idx: number, audioBlob: Blob): Promise<string | null> {
    try {
      if (!story?.id) return null
      const mime = audioBlob.type || "audio/mpeg"
      const ext = mime.includes("mpeg") || mime.includes("mp3") ? "mp3" : mime.includes("wav") ? "wav" : "mp3"
      const filePath = `stories/${story.id}/page-${idx + 1}.${ext}`
      const upResult = await supabase.storage.from("music").upload(filePath, audioBlob, { contentType: mime, upsert: true })
      if (upResult.error) {
        console.error("[Music] upload error", upResult.error)
        return null
      }
      const { data: publicData, error: publicErr } = await supabase.storage.from("music").getPublicUrl(filePath)
      if (publicErr) {
        console.error("[Music] getPublicUrl error", publicErr)
        return null
      }
      const publicUrl = (publicData as any)?.publicUrl ?? null
      if (!publicUrl) return null
      const updated = Array.isArray(segments) ? [...segments] : []
      updated[idx] = { ...updated[idx], music_url: publicUrl }
      setSegments(normalizeSegments(updated))
      return publicUrl
    } catch (e) {
      console.error("[saveMusicForPage] unexpected", e)
      return null
    }
  }

  // --- Helper: update story-level music_url on the stories table (keeps same name as before but now operates on story row) ---
async function supabaseContentSetMusicUrl(currentSegments: any[], idx: number, url: string) {
  try {
    // ignore currentSegments/idx (kept in signature for compatibility) and update story-level music_url
    const { error: updErr } = await supabase
      .from("stories")
      .update({ music_url: url })
      .eq("id", storyId);

    if (updErr) {
      console.error("[Music] Failed to update story music_url:", updErr);
      return false;
    }

    // re-fetch updated story and update local state
    const { data: freshStory, error: freshErr } = await supabase
      .from("stories")
      .select("*")
      .eq("id", storyId)
      .single();

    if (!freshErr && freshStory) {
      setStory(freshStory);
      setSegments(Array.isArray(freshStory.content) ? freshStory.content : []);
    }

    return true;
  } catch (e) {
    console.error("[Music] supabaseContentSetMusicUrl exception:", e);
    return false;
  }
}

// --- Save generated audio into Supabase storage and set stories.music_url (story-level) ---
async function saveMusicForPage(idx: number, audioBlob: Blob): Promise<string | null> {
  try {
    // choose extension from MIME
    const mime = audioBlob.type || "audio/mpeg";
    const ext = mime.includes("mpeg") || mime.includes("mp3")
      ? "mp3"
      : mime.includes("wav")
      ? "wav"
      : "mp3";

    // store as story-level file (not page-level)
    const filePath = `stories/${storyId}/story-music.${ext}`;

    // upload to Supabase storage
    const upResult = await supabase.storage.from("music").upload(filePath, audioBlob, {
      contentType: mime,
      upsert: true,
    });
    if (upResult.error) {
      console.error("[Music] Storage upload error:", upResult.error);
      return null;
    }

    // get public URL
    const { data: publicData, error: publicErr } = supabase.storage.from("music").getPublicUrl(filePath);
    if (publicErr) {
      console.error("[Music] getPublicUrl error:", publicErr);
      return null;
    }
    const publicUrl = (publicData as any)?.publicUrl ?? null;
    if (!publicUrl) {
      console.error("[Music] getPublicUrl returned no url");
      return null;
    }

    // update story-level music_url column
    const { error: updErr } = await supabase
      .from("stories")
      .update({ music_url: publicUrl })
      .eq("id", storyId);
    if (updErr) {
      console.error("[Music] Failed to update story music_url:", updErr);
      return null;
    }

    // re-fetch updated story and update local state
    const { data: freshStory, error: freshErr } = await supabase
      .from("stories")
      .select("*")
      .eq("id", storyId)
      .single();
    if (!freshErr && freshStory) {
      setStory(freshStory);
      setSegments(Array.isArray(freshStory.content) ? freshStory.content : []);
    }

    return publicUrl;
  } catch (e) {
    console.error("[Music] saveMusicForPage exception:", e);
    return null;
  }
}

// --- Generate music for all missing (story-level) music (single generator for whole story) ---
const generateAllMissingMusicFull = async () => {
  setIsGeneratingAllMusic(true);
  try {
    // fetch the story and read the story-level music_score and current music_url
    const { data: freshStory, error: fetchErr } = await supabase
      .from("stories")
      .select("*")
      .eq("id", storyId)
      .single();

    if (fetchErr || !freshStory) {
      console.error("[Music] Failed to fetch story before generation:", fetchErr);
      return;
    }

    // Only use the stories table music_score column (do not use per-page music_score)
    const storyPrompt = freshStory.music_score;
    const existingMusicUrl = freshStory.music_url;

    if (existingMusicUrl) {
      console.log("[Music] Story already has music_url, skipping generation:", existingMusicUrl);
      return;
    }

    if (!storyPrompt || typeof storyPrompt !== "string" || storyPrompt.trim().length === 0) {
      console.warn("[Music] No story-level music_score found; aborting full generation.");
      return;
    }

    console.log("[Music] Generating full story music with prompt:", storyPrompt);
    // call server-side music API (the same API you wired earlier)
    setIsGeneratingMusic(true);
    try {
      const res = await fetch("/api/music", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: storyPrompt, story_id: storyId }),
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error("[Music] /api/music returned error:", res.status, txt);
        return;
      }

      const contentType = res.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        const payload = await res.json();

        if (payload?.audio_url) {
          // direct audio URL from backend → update story.music_url
          const { error: updErr } = await supabase
            .from("stories")
            .update({ music_url: payload.audio_url })
            .eq("id", storyId);
          if (updErr) {
            console.error("[Music] JSONB update error after URL:", updErr);
          } else {
            const { data: fresh, error: freshErr } = await supabase
              .from("stories")
              .select("*")
              .eq("id", storyId)
              .single();
            if (!freshErr && fresh) {
              setStory(fresh);
              setSegments(Array.isArray(fresh.content) ? fresh.content : []);
            }
          }
          return;
        }

        if (payload?.audio_base64) {
          // convert base64 to Blob and save as story-level music
          const blob = await (await fetch(`data:audio/mpeg;base64,${payload.audio_base64}`)).blob();
          await saveMusicForPage(0, blob); // idx is unused for story-level saving
          return;
        }

        console.warn("[Music] JSON payload did not include audio_url or audio_base64");
        return;
      }

      // otherwise assume binary audio stream
      const audioBlob = await res.blob();
      if (!audioBlob || !audioBlob.size) {
        console.warn("[Music] No audio blob received");
        return;
      }
      await saveMusicForPage(0, audioBlob); // idx is unused for story-level saving
    } catch (e) {
      console.error("[Music] generateAllMissingMusicFull -> /api/music exception:", e);
    } finally {
      setIsGeneratingMusic(false);
    }
  } finally {
    setIsGeneratingAllMusic(false);
  }
}
// keep the stop signal ref handling you provided
const stopAllMusicSignalRef = useRef<number>(stopAllMusicSignal)
useEffect(() => { stopAllMusicSignalRef.current = stopAllMusicSignal }, [stopAllMusicSignal])

// Both functions now use the story-level music_url from the stories table (no segments, no stitching).
// They behave identically: fetch story.music_url, play that audio, respect stopAllMusicSignal, and do not modify audio.

// Preload state for story-level music URL
const [storyMusicUrl, setStoryMusicUrl] = useState<string | null>(null)
const [musicUrlLoading, setMusicUrlLoading] = useState(false)

// Keep storyMusicUrl in sync with story row when story object updates (fast path)
useEffect(() => {
  if (story?.music_url) {
    setStoryMusicUrl(String(story.music_url))
    return
  }
  // If story doesn't contain music_url, fetch it once (safe to do async here)
  let cancelled = false
  ;(async () => {
    try {
      setMusicUrlLoading(true)
      const { data: s, error } = await supabase.from("stories").select("music_url").eq("id", storyId).single()
      if (!error && s?.music_url && !cancelled) {
        setStoryMusicUrl(String(s.music_url))
      }
    } catch (err) {
      console.warn("[preloadMusic] fetch failed", err)
    } finally {
      if (!cancelled) setMusicUrlLoading(false)
    }
  })()
  return () => { cancelled = true }
}, [story?.music_url, storyId])

// Helper to attempt synchronous play using a known URL.
// IMPORTANT: Do NOT await anything before calling audio.play() in the click stack.
function playUrlImmediate(musicUrl: string, myStop: number) {
  try {
    // stop any currently playing audio
    try { playbackMusicRef.current?.pause() } catch {}
    playbackMusicRef.current = null

    const audio = new Audio(musicUrl)
    audio.crossOrigin = "anonymous"
    playbackMusicRef.current = audio

    // Set up finished promise (for background watcher)
    let finishedResolve: () => void
    const finished = new Promise<void>((resolve) => {
      finishedResolve = resolve
      audio.onended = () => resolve()
      audio.onerror = () => resolve()
    })

    // call play immediately (synchronously within same user event)
    const playPromise = audio.play()
    if (playPromise !== undefined) {
      // don't await here — handle rejection in catch
      playPromise.catch((err) => {
        console.warn("[playUrlImmediate] audio.play failed", err)
        // If playback is blocked, cleanup and notify user via UI (do not throw)
        try { audio.pause() } catch {}
        playbackMusicRef.current = null
        setIsPlayingAllMusic(false)
        // optional: show prompt to user to click again
      })
    }

    // Background watcher: waits for finish or stop signal, then cleans up.
    (async () => {
      try {
        await Promise.race([
          finished,
          new Promise<void>((resolve) => {
            const interval = setInterval(() => {
              if (stopAllMusicSignalRef.current !== myStop) {
                try { audio.pause() } catch {}
                clearInterval(interval)
                resolve()
              }
            }, 160)
          }),
        ])
      } catch (e) {
        console.error("[playUrlImmediate] watcher error", e)
      } finally {
        try { audio.pause() } catch {}
        playbackMusicRef.current = null
        setIsPlayingAllMusic(false)
      }
    })()

    return true
  } catch (e) {
    console.error("[playUrlImmediate] unexpected", e)
    return false
  }
}

// Full rewritten: playAllMusicSequential
const playAllMusicSequential = () => {
  // IMPORTANT: no `await` or async work before calling audio.play(),
  // so this function deliberately does the synchronous play in the click stack.
  if (isPlayingAllMusic) {
    setStopAllMusicSignal((s) => s + 1)
    return
  }

  // mark playing immediately (ok to set state)
  setIsPlayingAllMusic(true)
  const myStop = (stopAllMusicSignalRef.current ?? stopAllMusicSignal) + 1
  setStopAllMusicSignal(myStop)
  stopAllMusicSignalRef.current = myStop

  // Prefer the preloaded URL (synchronous). If present, play immediately.
  const url = storyMusicUrl ?? (story?.music_url ? String(story.music_url) : null)
  if (url) {
    const ok = playUrlImmediate(url, myStop)
    if (!ok) {
      // fallback: clear playing flag (playUrlImmediate already attempted cleanup)
      setIsPlayingAllMusic(false)
    }
    return
  }

  // If we get here, no preloaded URL was available.
  // Try to fetch in background and then attempt play — note: this is likely to be blocked;
  // so we notify user to click Play again if browser prevents playback.
  (async () => {
    try {
      const { data: s, error: fetchErr } = await supabase.from("stories").select("music_url").eq("id", storyId).single()
      if (fetchErr || !s?.music_url) {
        console.warn("[playAllMusicSequential] no story music_url after fetch", fetchErr)
        alert("No music available for this story.")
        setIsPlayingAllMusic(false)
        return
      }
      const fetchedUrl = String(s.music_url)
      setStoryMusicUrl(fetchedUrl)

      // Try to play once fetched. This may be blocked by autoplay policy — handle rejection.
      const ok = playUrlImmediate(fetchedUrl, myStop)
      if (!ok) {
        alert("Playback was blocked by the browser. Please click Play again to start the music.")
      }
    } catch (err) {
      console.error("[playAllMusicSequential] fetch+play failed", err)
      setIsPlayingAllMusic(false)
    }
  })()
}

// Rewritten: playAllMusicConcurrent (same behavior as sequential, but kept for parity)

const playAllMusicConcurrent = async () => {
  // Now does the exact same thing as sequential: fetch story.music_url and play it (no mixing/stitching)
  if (isPlayingAllMusic) {
    setStopAllMusicSignal((s) => s + 1)
    return
  }
  setIsPlayingAllMusic(true)
  const myStop = (stopAllMusicSignalRef.current ?? stopAllMusicSignal) + 1
  setStopAllMusicSignal(myStop)
  stopAllMusicSignalRef.current = myStop

  try {
    const { data: story, error: fetchErr } = await supabase
      .from("stories")
      .select("music_url")
      .eq("id", storyId)
      .single()

    if (fetchErr) {
      console.error("[playAllMusicConcurrent] failed to fetch story:", fetchErr)
      return
    }
    const musicUrl = story?.music_url
    if (!musicUrl) {
      console.warn("[playAllMusicConcurrent] no story music_url found")
      return
    }

    try {
      // stop any currently playing audio
      try { playbackMusicRef.current?.pause() } catch {}
      playbackMusicRef.current = null

      const audio = new Audio(musicUrl)
      audio.crossOrigin = "anonymous"
      playbackMusicRef.current = audio

      const finished = new Promise<void>((resolve) => {
        audio.onended = () => resolve()
        audio.onerror = () => resolve()
      })

      await audio.play().catch((e) => {
        console.warn("[playAllMusicConcurrent] audio.play failed", e)
      })

      // wait for end or stop signal
      await Promise.race([
        finished,
        new Promise<void>((resolve) => {
          const interval = setInterval(() => {
            if (stopAllMusicSignalRef.current !== myStop) {
              try { audio.pause() } catch {}
              clearInterval(interval)
              resolve()
            }
          }, 160)
        }),
      ])

      try { audio.pause() } catch {}
      playbackMusicRef.current = null
    } catch (e) {
      console.error("[playAllMusicConcurrent] playback error", e)
    }
  } catch (e) {
    console.error("[playAllMusicConcurrent] outer error", e)
  } finally {
    setIsPlayingAllMusic(false)
    playbackMusicRef.current = null
  }
}

  // export PDF
  const exportPDF = async () => {
    try {
      if (translationLoading) { alert("Please wait for translation to finish before exporting PDF."); return }
      const { jsPDF } = await import("jspdf").catch((e) => { console.error("[exportPDF] import failed", e); return { jsPDF: null as any } })
      if (!jsPDF) { alert("PDF export not available."); return }
      const doc = new jsPDF("p", "pt", "a4")
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const marginX = 60
      const marginY = 60
      const contentWidth = pageWidth - marginX * 2
      const centerX = pageWidth / 2

      // cover
      try {
        if (story?.cover_image_url) {
          const dataURL = await fetchImageToDataURLSafe(story.cover_image_url)
          const imgFormat = dataURL.includes("png") ? "PNG" : "JPEG"
          doc.addImage(dataURL, imgFormat, 0, 0, pageWidth, pageHeight, undefined, "FAST")
        } else {
          doc.setFillColor(250, 245, 230)
          doc.rect(0, 0, pageWidth, pageHeight, "F")
        }
      } catch (e) {
        console.warn("[exportPDF] cover add error", e)
        doc.setFillColor(250, 245, 230)
        doc.rect(0, 0, pageWidth, pageHeight, "F")
      }

      doc.setFontSize(28)
      doc.setTextColor(30, 20, 10)
      doc.text(story?.title ?? "Untitled", centerX, 80, { align: "center" })
      doc.setFontSize(14)
      doc.setTextColor(80, 60, 40)
      doc.text(fullName ?? " ", centerX, pageHeight - 60, { align: "center" })

      for (let i = 0; i < (segments?.length ?? 0); i++) {
        doc.addPage()
        const seg = segments?.[i] ?? {}
        let y = marginY
        doc.setFontSize(18)
        doc.setTextColor(30, 20, 10)
        doc.text(`Scene ${i + 1}`, centerX, y, { align: "center" })
        y += 28
        if (seg?.image_url) {
          try {
            const dataURL = await fetchImageToDataURLSafe(seg.image_url)
            const img = await loadImage(dataURL)
            if (img) {
              const maxW = contentWidth
              const maxH = 240
              const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1)
              const w = img.naturalWidth * ratio
              const h = img.naturalHeight * ratio
              const x = centerX - w / 2
              const imgFormat = dataURL.includes("png") ? "PNG" : "JPEG"
              doc.addImage(dataURL, imgFormat, x, y, w, h, undefined, "FAST")
              y += h + 12
            }
          } catch (e) {
            console.warn("[exportPDF] image add failed for page", i + 1, e)
          }
        }
        doc.setFontSize(12)
        doc.setTextColor(10, 10, 10)
        const text = typeof seg?.story_segment === "string" ? seg.story_segment : ""
        const para = doc.splitTextToSize(text, contentWidth)
        const lineHeight = 16
        for (let li = 0; li < para.length; li++) {
          if (y > pageHeight - marginY) { doc.addPage(); y = marginY }
          doc.text(para[li], marginX, y)
          y += lineHeight
        }
      }
      const filename = (story?.title ?? "story").replace(/[/?%*:|"<>]/g, "-").slice(0, 120)
      doc.save(`${filename}.pdf`)
    } catch (e) {
      console.error("[exportPDF] unexpected", e)
      alert("Failed to export PDF. See console.")
    }
  }
  

  // video modals - uses both story.video_url and videoUrl local state so if DB has a video we show it
  const openVideoFullscreen = () => {
    if (!(story?.video_url || videoUrl)) {
      alert("No video available")
      return
    }
    setVideoIsFullScreen(true)
  }
  const closeVideoFullscreen = () => setVideoIsFullScreen(false)

  // inject UI styles (small set) - unchanged aside from prior changes
  useEffect(() => {
    if (document.getElementById("story-gold-ui")) return
    const s = document.createElement("style")
    s.id = "story-gold-ui"
    s.innerHTML = `
      /* Important gold buttons (ONLY these keep full gold) */
      .gold-btn {
        border-radius: 12px;
        padding: 10px 14px;
        background: linear-gradient(90deg,#FFD84A,#FFB703);
        color: #000;
        font-weight: 800;
        border: 2px solid rgba(255,215,64,0.95);
        box-shadow: 0 8px 30px rgba(255,200,60,0.18), 0 0 40px rgba(255,200,60,0.08);
        transition: transform .12s ease, box-shadow .12s ease;
      }
      .gold-btn:hover { transform: translateY(-3px); box-shadow: 0 22px 60px rgba(255,200,60,0.22); }

      /* Accent outline buttons: gold rim plus a gentle color variation */
      .accent-outline-btn {
        border-radius: 12px;
        padding: 10px 12px;
        color: #FFF8EA;
        border: 2px solid rgba(255,215,64,0.88);
        box-shadow: 0 6px 22px rgba(0,0,0,0.45), 0 4px 18px rgba(255,215,64,0.06) inset;
        transition: transform .12s ease, box-shadow .12s ease, background .12s ease;
        background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.04));
      }
      .accent-outline-btn:hover { transform: translateY(-3px); box-shadow: 0 12px 34px rgba(0,0,0,0.55), 0 10px 30px rgba(255,215,64,0.08); }

      /* color variants for non-gold buttons - still rimmed with gold */
      .accent-blue { background: linear-gradient(180deg, rgba(10,20,40,0.65), rgba(18,30,60,0.7)); }
      .accent-purple { background: linear-gradient(180deg, rgba(36,18,54,0.6), rgba(26,12,46,0.75)); }
      .accent-teal { background: linear-gradient(180deg, rgba(6,40,45,0.62), rgba(8,34,36,0.7)); }
      .accent-green { background: linear-gradient(180deg, rgba(16,44,22,0.6), rgba(8,32,16,0.75)); }

      .control-row { display:flex; gap:8px; align-items:center; flex-wrap:wrap; justify-content:center; }
      /* center controls on mobile and desktop beneath the book */
      @media (max-width:820px) { .control-row { justify-content:center; } }
      @media (min-width:821px) { .control-row { justify-content:center; } }

      .edge-arrow {
        position: absolute; top:50%; transform: translateY(-50%); z-index:160; cursor:pointer;
        width:62px; height:72px; display:flex; align-items:center; justify-content:center; border-radius:12px;
        background: linear-gradient(180deg, rgba(0,0,0,0.28), rgba(0,0,0,0.45));
        border: 2px solid rgba(255,215,64,0.18); color:#FFD166; box-shadow: 0 12px 44px rgba(0,0,0,0.6);
      }
      @media (max-width:820px) { .edge-arrow { width:44px; height:56px } }
    `
    document.head.appendChild(s)
  }, [])

  // cleanup
  useEffect(() => {
    return () => {
      try { playbackMusicRef.current?.pause() } catch {}
      try { playbackTTSRef.current?.pause() } catch {}
      setIsPlayingAllMusic(false)
    }
  }, [])

  const coverImage = story?.cover_image_url ?? story?.cover_image ?? null
  const coverTitle = story?.title ?? "Untitled"
  const isValidMusicUrl = (url?: string | null) => {
  if (!url || typeof url !== "string") return false;
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:" || u.protocol === "data:";
  } catch {
    return false;
  }
};

const musicUrl: string = (story?.music_url as string) ?? "";
const hasMusic = isValidMusicUrl(musicUrl);


  /* MAIN RENDER
     NOTE: show Open Video Fullscreen if story.video_url exists OR local videoUrl state set.
  */
  return (
    <div style={{ minHeight: "100vh", width: "100%", background: "linear-gradient(180deg,#050405,#0b0b0a)", color: "#FFF8EA", padding: 28, boxSizing: "border-box", display: "flex", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 1200 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
              <h1 style={{ margin: 0, fontSize: 28, fontWeight: 900, color: "#FFF8EA" }}>{story?.title ?? "Untitled"}</h1>
              <div style={{ padding: "6px 10px", borderRadius: 8, background: "linear-gradient(90deg, rgba(255,215,64,0.08), rgba(255,200,40,0.04))", color: "#FFE9A8", fontWeight: 700, fontSize: 12 }}>
                {story?.is_public ? "Public" : "Private"}
              </div>
            </div>
            <div style={{ color: "#E9D8A6", marginTop: 6 }}>{fullName || "Unknown Author"}</div>
            <div style={{ color: "#9CA3AF", fontSize: 12, marginTop: 6 }}>Created: {story?.created_at ? new Date(story.created_at).toLocaleString() : "Unknown"}</div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={handleLike} className="accent-outline-btn accent-green" title={liked ? "Unlike" : "Like" } style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={liked ? "#FFD166" : "none"} stroke={liked ? "#FFD166" : "#FFD166"} strokeWidth="1.1"><path d="M12 21s-6.716-4.8-9-8.2A5.6 5.6 0 0 1 4 6.6a5.4 5.4 0 0 1 8-1.1A5.4 5.4 0 0 1 20 6.6c.9 4.56-9 12.4-9 12.4z" /></svg>
              <span style={{ fontWeight: 800 }}>{likesCount}</span>
            </button>

            <button onClick={exportPDF} className="accent-outline-btn accent-blue">Export PDF</button>

            <button onClick={() => setUseVideoBlobWorkaround((s) => !s)} className="accent-outline-btn accent-teal">{useVideoBlobWorkaround ? "Video: Blob" : "Video: Direct"}</button>
          </div>
        </div>

        {/* Book area */}
        <div style={{ display: "flex", gap: 18, justifyContent: "center", alignItems: "flex-start", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "center", position: "relative", width: "100%" }}>
            <div style={{ position: "relative", width: isMobile ? "100%" : 1100 }}>
              <div className="edge-arrow" style={{ left: isMobile ? 8 : -84 }} onClick={goPrev} title="Previous page">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFD166" strokeWidth="1.6"><path d="M15 18l-6-6 6-6" /></svg>
              </div>

              <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <FlipBookAnimated ref={flipBookRef as any} segments={segments} coverTitle={coverTitle} coverImageUrl={coverImage} coverFullName={fullName} isMobile={isMobile} />
              </div>

              <div className="edge-arrow" style={{ right: isMobile ? 8 : -84 }} onClick={goNext} title="Next page">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFD166" strokeWidth="1.6"><path d="M9 6l6 6-6 6" /></svg>
              </div>
            </div>
          </div>


            {/* Important buttons (gold) centered below the book */}
    <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: 10 }}>
      <div className="control-row" style={{ width: isMobile ? "100%" : 760 }}>
        <button className="gold-btn" onClick={goPrev} style={{ minWidth: 90 }}>Prev</button>
        <button className="gold-btn" onClick={goNext} style={{ minWidth: 90 }}>Next</button>

        {/* Always show read page (TTS only) */}
        <button
          className="gold-btn"
          onClick={readAloud}
          disabled={isSpeaking}
          style={{ minWidth: 130 }}
        >
          {isSpeaking ? "Reading..." : "Read Page"}
        </button>

        {/* Show Read + Music only when the story-level music_url is a valid URL */}
        {hasMusic && (
          <button
            className="gold-btn"
            onClick={readAloudWithMusic}
            style={{ minWidth: 130 }}
          >
            Read + Music
          </button>
        )}
      </div>
    </div>

    {/* Secondary row under the book - show Generate buttons only when there's NO valid music_url.
        Always keep the Play button visible only when hasMusic is true. */}
    <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: 8 }}>
      <div className="control-row" style={{ width: isMobile ? "100%" : 760 }}>

        {/* If there is no valid story-level music URL, show generation options */}
        {!hasMusic && (

            <button
              className="accent-outline-btn accent-purple"
              onClick={generateAllMissingMusicFull}
              disabled={isGeneratingAllMusic}
            >
              {isGeneratingAllMusic ? "Generating Music..." : "Generate Music"}
            </button>
        
        )}

        {/* Play Music Score — only shown when story-level music_url is valid */}
        {hasMusic && (
          <button
            className="gold-btn"
            onClick={playAllMusicSequential}
          >
            {isPlayingAllMusic ? "Stop Music" : "Play Music Score"}
          </button>
        )}
      </div>
    </div>


          {/* Controls block placed BELOW the book (ensures it's not behind the book on desktop) */}
          <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: 16 }}>
            <div style={{ width: isMobile ? "100%" : 980, display: "flex", justifyContent: "center" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                <select value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(255,215,64,0.08)", background: "rgba(0,0,0,0.35)", color: "#FFF8EA" }}>
                  {languages.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>

                <select value={voice} onChange={(e) => setVoice(e.target.value)} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(255,215,64,0.08)", background: "rgba(0,0,0,0.35)", color: "#FFF8EA" }}>
                  <option value="default">Rachel</option>
                  <option value="robot">Adam</option>
                  <option value="fairy">Dorothy</option>
                </select>

                <button className="accent-outline-btn accent-teal" onClick={() => setUseVideoBlobWorkaround((s) => !s)}>{useVideoBlobWorkaround ? "Video: Blob" : "Video: Direct"}</button>

                <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#E9D8A6" }}>
                  <input type="checkbox" checked={stereoMix} onChange={(e) => setStereoMix(e.target.checked)} />
                  Stereo mix
                </label>

                <button className="accent-outline-btn accent-blue" onClick={exportPDF}>Export PDF</button>

                {/* Use story.video_url OR videoUrl state to decide whether to show open-video UI */}
                { (story?.video_url || videoUrl) ? (
                  <button className="accent-outline-btn accent-purple" onClick={openVideoFullscreen}>Open Video Fullscreen</button>
                ) : (
                  <button className="accent-outline-btn accent-purple" onClick={async () => {
                    setIsGenerating(true)
                    try {
                      const prompts = Array.isArray(segments) ? segments.map((seg: any) => ({ prompt: seg?.video_description ?? seg?.story_segment ?? "", options: { durationSeconds: 8 } })) : []
                      const res = await fetch("/api/generate-veo", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ story_id: story?.id ?? storyId, prompts }) })
                      if (!res.ok) throw new Error(`API error: ${res.status}`)
                      await res.json()
                      alert("Video generation queued. It may take a while; video will appear when ready.")
                    } catch (err) {
                      console.error("[generate video] failed", err)
                      alert("Failed to queue video generation. See console.")
                    } finally {
                      setIsGenerating(false)
                    }
                  }}>Generate Video</button>
                )}

                <button className="accent-outline-btn accent-green" onClick={togglePublic} disabled={isPublicUpdating}>{isPublicUpdating ? "Updating..." : (story?.is_public ? "Set Private" : "Set Public")}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video fullscreen modal */}
      {(videoIsFullScreen && (story?.video_url || videoUrl)) && (
        <div style={{ position: "fixed", inset: 0, zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.92)" }}>
          <button onClick={closeVideoFullscreen} style={{ position: "absolute", top: 18, right: 18, zIndex: 10010, padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.06)", color: "#FFF8EA" }}>Close</button>
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            { /\.mp4(\?|$)/i.test((story?.video_url ?? videoUrl) || "") ? (
              <video src={story?.video_url ?? videoUrl ?? ""} controls autoPlay style={{ maxWidth: "100%", maxHeight: "100%" }} />
            ) : (
              <iframe src={story?.video_url ?? videoUrl ?? ""} title="fullscreen video" style={{ width: "100%", height: "100%", border: "none" }} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
